import { css } from '@emotion/core';
import { useAlert } from '@weave/alert-system';
import {
  ButtonBar,
  CheckboxField,
  ContentLoader,
  DatePickerField,
  DropdownField,
  EmailField,
  FormFieldActionTypes,
  FormRow,
  Heading,
  IconButton,
  Modal,
  PostalCodeField,
  PostalCodeLocale,
  PrimaryButton,
  SecondaryButton,
  SpinningLoader,
  Text,
  TextareaField,
  TextField,
  TextLink,
  TrashIcon,
  useControlledField,
  useForm,
  useFormField,
  useModalControl,
  ValidatorFieldState,
} from '@weave/design-system';
import {
  MediaData,
  PortingData,
  PortingRequest,
} from '@weave/schema-gen-ts/dist/schemas/phone/porting/porting-data/v1/porting_data_service.pb';
import {
  NumberType,
  PortingType,
  PortType,
} from '@weave/schema-gen-ts/dist/shared/porting/v1/enums.pb';
import { theme } from '@weave/theme-original';
import { format } from 'date-fns';
import fileDownload from 'js-file-download';
import { noop } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  portingApi as API,
  portingSchemaApi,
  portViewBillDownload,
} from '../../../apis/porting/porting.api';
import { PortingQueries } from '../../../apis/porting/porting.queries';
import {
  MediaType as OldMediaType,
  PortCreationFileResponseData,
  PortingMediaType,
} from '../../../apis/porting/porting.types';
import { injectParams, ONBOARDING } from '../../../constants/paths';
import { selectCurrentLocation } from '../../../redux/actions/location';
import { Page } from '../../shared';
import { stateCodes, stateCodeType } from '../porting-card/state-codes';
import { MessageDialogue } from '../utils/message-dialogue.component';
import { CountryCodes } from '../utils/port-constants';
import {
  buildRequestedNumberTypesMap,
  convertUsDateToIsoDateFormat,
  getConflictingExistingNumbers,
  getBlackoutDates,
  getErrorMessageFromResponseError,
  getFormattedNumbers,
  getUTCDateWithPortingTimeString,
  isAlphaNumericValue,
  isHoliday,
  isWeekend,
  splitAndTrimString,
  validatePhoneNumbersRegex,
} from '../utils/porting-utils';
import { PortOrderUploadComponent } from './port-order-upload.component';
import { portOrderStyles } from './port-order.styles';
import dayjs from 'dayjs';

type SortedFiles = { accepted: File[]; rejected: File[]; duplicates: File[] };

type MessageModal = {
  header: string;
  body: string;
  success: boolean;
};

type ExtendedMediaData = MediaData & { fileType: string };

const successMessage = `Your port order information has been created.`;

export const PortOrder: FC = () => {
  const alert = useAlert();
  const [loaLoader, setLoaLoader] = useState<boolean>(false);
  const [billLoader, setBillLoader] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [filteredStates, setFilteredStates] = useState<stateCodeType[]>([]);
  const [voiceNumbers, setVoiceNumbers] = useState<string>('');
  const [faxNumbers, setFaxNumbers] = useState<string>('');
  const [smsNumbers, setSmsNumbers] = useState<string>('');
  const [smsNumberValues, setSmsNumberValues] = useState<string[]>();
  const [phoneNumbersError, setPhoneNumbersError] = useState<string>('');
  const [requestPortDateError, setRequestPortDateError] = useState<string>('');
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const currentLocation = useSelector(selectCurrentLocation);
  const history = useHistory();

  const {
    modalProps: portSubmissionModalProps,
    closeModal: closePortSubmissionModal,
    triggerProps: portSubmissionTriggerModalProps,
  } = useModalControl();
  const {
    modalProps: cancelConfirmationModalProps,
    closeModal: closeCancelModal,
    triggerProps: triggerCancelProps,
  } = useModalControl();
  const {
    modalProps: cancelInternalPorting,
    triggerProps: triggerCancelInternalPortingProps,
  } = useModalControl();
  const [messageModal, setMessageModal] = useState<MessageModal>({
    header: 'Thank You',
    body: successMessage,
    success: true,
  });
  const [portingData, setPortingData] = useState<PortingData>({} as PortingData);
  const [attachedLOA, setAttachedLOA] = useState<ExtendedMediaData[]>([]);
  const [attachedBills, setAttachedBills] = useState<ExtendedMediaData[]>([]);
  const [noBillAvailable, setNoBillAvailable] = useState<boolean>(false);
  const [customerAttachedFiles, setCustomerAttachedFiles] = useState<ExtendedMediaData[]>(
    []
  );
  const [internalPortingValue, setInternalPortingValue] =
    useState<string>('PORTING_TYPE_MAIN');
  const [voiceNumberValues, setVoiceNumberValues] = useState<string[]>();
  const [faxNumberValues, setFaxNumberValues] = useState<string[]>();
  const [portingNumberProviderData, setPortingNumberProviderData] = useState<
    { number: string; provider: string; isFax: boolean; faxProvider: string }[]
  >([]);
  const [internalPortingNumberError, setInternalPortingNumberError] =
    useState<string>('');
  const [internalPortingFaxError, setInternalPortingFaxError] = useState<string>('');
  const [autoFillDetails, setAutoFillDetails] = useState<{
    companyName: string;
    serviceAddress: string;
    city: string;
    postalCode: string;
    state: string;
    country: string;
    authorizedUserFullName: string;
    accountNumber: string;
  }>({
    companyName: '',
    serviceAddress: '',
    city: '',
    postalCode: '',
    state: '',
    country: '',
    authorizedUserFullName: '',
    accountNumber: '',
  });
  const [uploadedFileUrl, setUploadedFileUrl] = useState<object>({});
  const [downloadLOALoading, setDownloadLOALoading] = useState<boolean>(false);
  const [duplicateFaxError, setDuplicateFaxError] = useState<boolean>(false);
  const [duplicatePhnError, setDuplicatePhnError] = useState<boolean>(false);
  const [duplicateSmsError, setDuplicateSmsError] = useState<boolean>(false);
  const providerNumMapRef = useRef(new Map());
  const providerFaxMapRef = useRef(new Map());

  const providerNumMap = providerNumMapRef.current;
  const providerFaxMap = providerFaxMapRef.current;

  const isPortingTypeInternal = internalPortingValue === 'PORTING_TYPE_INTERNAL';

  const checkNoBillField = useFormField({ type: 'checkbox' });
  const commentField = useFormField({ type: 'text' });

  const validationFailed = (providerNumMap) => {
    return providerNumMap.size !== 1;
  };

  const handleValidationError = (providerMap) => {
    let error = 'Please make sure all the numbers belong to the same provider but here';
    for (const [key, value] of providerMap) {
      error = error + `${value} belongs to provider ${key} `;
    }
    providerNumMap.size > 1 && setInternalPortingNumberError(error);
    providerFaxMap.size > 1 && setInternalPortingFaxError(error);
  };

  const validateInternalPortingNumbers = (numValues, providersMap) => {
    if (numValues && isPortingTypeInternal) {
      portingNumberProviderData?.forEach((existingPortingNumbers) => {
        const mapData =
          existingPortingNumbers?.provider ||
          (existingPortingNumbers?.isFax === true && existingPortingNumbers?.faxProvider);

        if (numValues.indexOf(existingPortingNumbers.number) !== -1) {
          if (
            typeof existingPortingNumbers.provider === 'undefined' &&
            typeof existingPortingNumbers.faxProvider === 'undefined'
          ) {
            return;
          } else {
            if (providersMap.has(mapData)) {
              providersMap.set(
                mapData,
                providersMap.get(mapData) + ',' + existingPortingNumbers.number
              );
            } else {
              providersMap.set(
                existingPortingNumbers?.provider ||
                  (existingPortingNumbers.isFax === true &&
                    existingPortingNumbers?.faxProvider),
                existingPortingNumbers.number
              );
            }
          }
        }
      });
      if (validationFailed(providersMap)) {
        handleValidationError(providersMap);
      }
    }
  };

  const getAvailableProvider = (portingProvider) => {
    if (typeof portingProvider === 'undefined') {
      return;
    }
    let value = portingProvider?.includes('_to_')
      ? portingProvider?.split('_to_')[0]
      : portingProvider;
    value = value?.charAt(0)?.toUpperCase() + value?.slice(1);
    return value;
  };

  const getInternalPortingNumberData = async () => {
    const locationID = currentLocation?.ParentID
      ? currentLocation?.ParentID
      : currentLocation?.LocationID;
    // This /tenant/phonenumbers endpoint is not a porting endpoint.
    const response = await API.getInternalPortingNumberData(locationID);
    const newPortingData = response?.map((portingData) => ({
      number: portingData.Number,
      provider: getAvailableProvider(
        portingData?.VoiceProvider?.provider ||
          portingData?.SMSProvider?.provider ||
          portingData?.E911Provider?.provider
      ),
      isFax: portingData.IsFax,
      faxProvider: getAvailableProvider(portingData?.FaxProvider?.provider),
    }));
    setPortingNumberProviderData(newPortingData);
  };

  useEffect(() => {
    validateInternalPortingNumbers(voiceNumberValues, providerNumMap);
  }, [voiceNumberValues, portingNumberProviderData]);

  useEffect(() => {
    validateInternalPortingNumbers(faxNumberValues, providerFaxMap);
  }, [faxNumberValues, portingNumberProviderData]);

  const findDuplicateValue = (arr1: string[], arr2: string[]): string[] => {
    return arr1?.filter((arr1Item) => arr2.includes(arr1Item));
  };

  useEffect(() => {
    const voiceNumberArray = voiceNumbers.split(',');
    if (faxNumberValues) {
      const result = findDuplicateValue(faxNumberValues, voiceNumberArray);
      setDuplicatePhnError(!!result?.length);
    }
    if (!voiceNumbers && isPortingTypeInternal) {
      providerNumMap.clear();
      setInternalPortingNumberError('');
      setVoiceNumberValues([]);
    }
  }, [voiceNumbers]);

  useEffect(() => {
    const faxNumberArray = faxNumbers.split(',');
    if (voiceNumberValues) {
      const result = findDuplicateValue(voiceNumberValues, faxNumberArray);
      setDuplicatePhnError(!!result?.length);
    }
    if (!faxNumbers && isPortingTypeInternal) {
      providerFaxMap.clear();
      setInternalPortingFaxError('');
      setFaxNumberValues([]);
    }
  }, [faxNumbers]);

  useEffect(() => {
    const smsNumbersArray = smsNumbers
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);
    const hasDuplicates = smsNumbersArray.some(
      (val, i) => smsNumbersArray.indexOf(val) !== i
    );
    setDuplicateSmsError(hasDuplicates);
  }, [smsNumbers]);

  const today = new Date();
  const voiceNumbersFieldProps = useControlledField({
    type: 'text',
    value: voiceNumbers,
    onChange: (value) => {
      setVoiceNumbers(getFormattedNumbers(value)), setDuplicatePhnError(false);
    },
    validator: ({ value, active }: ValidatorFieldState<'text'>) => {
      if (!active) {
        if (!validatePhoneNumbersRegex(value)) {
          const msg = 'Please enter valid phone numbers';
          return msg;
        } else {
          setVoiceNumberValues([...value.split(',')]);
        }
        setPhoneNumbersError('');
        setInternalPortingNumberError('');
      }
      return '';
    },
  });
  const smsNumbersFieldProps = useControlledField({
    type: 'text',
    value: smsNumbers,
    onChange: (value) => {
      setSmsNumbers(getFormattedNumbers(value));
      setDuplicateSmsError(false);
    },
    validator: ({ value, active }: ValidatorFieldState<'text'>) => {
      if (!active) {
        if (!validatePhoneNumbersRegex(value)) {
          return 'Please enter valid SMS phone numbers';
        } else {
          setSmsNumberValues([...value.split(',')]);
        }
        setPhoneNumbersError('');
      }
      return '';
    },
  });
  const faxNumbersFieldProps = useControlledField({
    type: 'text',
    value: faxNumbers,
    onChange: (value) => {
      setFaxNumbers(getFormattedNumbers(value)), setDuplicateFaxError(false);
    },
    validator: ({ value, active }: ValidatorFieldState<'text'>) => {
      if (!active) {
        if (!validatePhoneNumbersRegex(value)) {
          const msg = 'Please enter valid fax numbers';
          return msg;
        } else {
          setFaxNumberValues([...value.split(',')]);
        }
        setPhoneNumbersError('');
        setInternalPortingFaxError('');
      }
      return '';
    },
  });

  const getIntialValues = () => {
    isPortingTypeInternal &&
      setAutoFillDetails({
        companyName: 'Weave',
        serviceAddress: '1331 W Powell Way',
        city: 'Lehi',
        country: 'US',
        state: 'UT',
        postalCode: '84043',
        accountNumber: voiceNumberValues
          ? voiceNumberValues?.[0]
          : faxNumberValues
          ? faxNumberValues?.[0]
          : '',
        authorizedUserFullName: 'Reid Dwiggins',
      });
  };

  const validateDataForInternalPortingCheckbox = (state) => {
    const cityValue =
      state?.city?.value?.toString()?.toLowerCase() !==
      autoFillDetails.city.toLowerCase();

    const companyNameValue =
      state?.companyName?.value?.toString()?.toLowerCase() !==
      autoFillDetails.companyName.toLowerCase();

    const postalCodeValue =
      state?.postalCode?.value?.toString()?.toLowerCase() !==
      autoFillDetails.postalCode.toLowerCase();

    const serviceAddressValue =
      state?.serviceAddress?.value.toString()?.toLowerCase() !==
      autoFillDetails.serviceAddress.toLowerCase();

    const stateValue =
      state?.state?.value?.toString()?.toLowerCase() !==
      autoFillDetails.state.toLowerCase();

    const authorizedUserFullNameValue =
      state?.authorizedUserFullName?.value.toString()?.toLowerCase() !==
      autoFillDetails.authorizedUserFullName.toLowerCase();

    const countryValue =
      state?.country?.value?.toString()?.toLowerCase() !==
      autoFillDetails.country.toLowerCase();

    const accountNumValue =
      state?.accountNumber?.value?.toString()?.toLowerCase() !==
      autoFillDetails.accountNumber.toLowerCase();

    return (
      cityValue ||
      companyNameValue ||
      postalCodeValue ||
      serviceAddressValue ||
      stateValue ||
      authorizedUserFullNameValue ||
      countryValue ||
      accountNumValue
    );
  };

  const minDate = format(new Date(), 'MM/dd/yyyy');
  const maxDate = dayjs().add(30, 'day');
  const blackoutDates = useMemo(() => getBlackoutDates(), []);
  const {
    formProps: portingFormProps,
    getFieldProps,
    seedValues,
    isComplete,
    values,
  } = useForm({
    fields: {
      companyName: {
        type: 'text',
        required: true,
      },
      serviceAddress: {
        type: 'text',
        required: true,
      },
      city: {
        type: 'text',
        required: true,
      },
      postalCode: {
        type: 'postalCode',
        required: true,
      },
      state: {
        type: 'dropdown',
        required: true,
      },
      country: {
        type: 'dropdown',
        required: true,
      },
      phoneProvider: {
        type: 'text',
        required: true,
      },
      accountNumber: {
        type: 'text',
        required: false,
      },
      portOrderType: {
        type: 'dropdown',
        value: PortingType.PORTING_TYPE_MAIN,
      },
      authorizedUserFullName: {
        type: 'text',
        required: true,
      },
      userOfficeEmail: {
        type: 'email',
        required: true,
      },
      accountPin: {
        type: 'text',
      },
      internalPorting: {
        type: 'checkbox',
      },
      asapPort: {
        type: 'checkbox',
      },
      requestedPortDate: {
        type: 'datePicker',
        minDate: getUTCDateWithPortingTimeString(today.toISOString()),
        maxDate: getUTCDateWithPortingTimeString(maxDate.toISOString()),
        blackoutDates: blackoutDates,
        validator: ({ value }: ValidatorFieldState<'datePicker'>) => {
          const formattedValue = convertUsDateToIsoDateFormat(value);
          const formattedMinDate = convertUsDateToIsoDateFormat(minDate);
          const formattedMaxDate = maxDate.format('YYYY/MM/DD');

          if (formattedValue < formattedMinDate) {
            return 'Requested port date cannot be set in the past';
          }
          if (formattedValue > formattedMaxDate) {
            return 'The requested port date must be within 30 days from today.';
          }
          return '';
        },
      },
    },
    fieldStateReducer: (state, action) => {
      if (action.type === FormFieldActionTypes.Blur) {
        switch (action.payload.name) {
          case 'accountNumber': {
            const isSms = state.portOrderType?.value === PortingType.PORTING_TYPE_UNKNOWN;
            if (isSms) {
              return {
                ...state,
                accountNumber: {
                  ...state.accountNumber,
                  error: '',
                  required: false,
                },
              };
            }
            if (typeof state.accountNumber.value === 'string') {
              let updatedVal = state.accountNumber.value;
              if (!isAlphaNumericValue(state.accountNumber.value)) {
                updatedVal = state.accountNumber.value.replace(/[^a-zA-Z0-9]/g, '');
              }
              return {
                ...state,
                accountNumber: {
                  ...state.accountNumber,
                  value: updatedVal,
                  error: updatedVal ? '' : 'This field is required',
                  required: true,
                },
              };
            }
          }
        }
      }
      if (action.type === FormFieldActionTypes.Update) {
        switch (action.payload.name) {
          case 'portOrderType': {
            const isSms = action.payload.value === PortingType.PORTING_TYPE_UNKNOWN;
            return {
              ...state,
              portOrderType: {
                ...state.portOrderType,
                value: action.payload.value,
              },
              accountNumber: {
                ...state.accountNumber,
                required: !isSms,
                error: isSms
                  ? ''
                  : !state.accountNumber.value
                  ? 'This field is required'
                  : state.accountNumber.error,
              },
            };
          }
          case 'internalPorting': {
            if (action.payload.value && validateDataForInternalPortingCheckbox(state)) {
              action.payload.value = false;
              triggerCancelInternalPortingProps.onClick();
            }
            return {
              ...state,
              internalPorting: {
                ...state.internalPorting,
                value: action.payload.value,
              },
            };
          }
          case 'country': {
            const filterStates = stateCodes.filter(
              (st: any) => action.payload.value === st.country
            );
            setFilteredStates(filterStates);
            return {
              ...state,
              postalCode: {
                ...state.postalCode,
                locale:
                  action.payload.value === PostalCodeLocale.CA
                    ? PostalCodeLocale.CA
                    : PostalCodeLocale.US,
                value: '',
              },
              state: {
                ...state.state,
                value: '',
              },
            };
          }
          default:
            return null;
        }
      }
      return null;
    },
    onSubmit: (values) => handleSubmit(values),
  });
  const { country } = values;
  const checkCountryValue: boolean = country !== PostalCodeLocale.US;

  const isSmsHosting = values?.portOrderType === PortingType.PORTING_TYPE_UNKNOWN; // NUMBER_TYPE_SMS refers to Sms Hosting or we can check enum value

  const phoneNumbersToCheck = useMemo(() => {
    if (isSmsHosting) {
      return smsNumberValues ?? [];
    }
    return [...(voiceNumberValues ?? []), ...(faxNumberValues ?? [])];
  }, [voiceNumberValues, faxNumberValues, smsNumberValues, isSmsHosting]);

  const requestedNumberTypesByNumber = useMemo(() => {
    if (isSmsHosting) {
      const numbersWithTypes = (smsNumberValues ?? []).map((phoneNumber) => ({
        phoneNumber,
        numberType: NumberType.NUMBER_TYPE_SMS,
      }));
      return buildRequestedNumberTypesMap(numbersWithTypes);
    }

    const numbersWithTypes = [
      ...(voiceNumberValues ?? []).map((phoneNumber) => ({
        phoneNumber,
        numberType: NumberType.NUMBER_TYPE_PHONE,
      })),
      ...(faxNumberValues ?? []).map((phoneNumber) => ({
        phoneNumber,
        numberType: NumberType.NUMBER_TYPE_FAX,
      })),
    ];

    return buildRequestedNumberTypesMap(numbersWithTypes);
  }, [voiceNumberValues, faxNumberValues, smsNumberValues, isSmsHosting]);

  const { data: existingNumbers, isLoading: isValidatingNumbers } =
    PortingQueries.useCheckPhoneNumbersExists(
      { numbers: phoneNumbersToCheck },
      {
        enabled: !isPortingTypeInternal,
        select: (response) =>
          getConflictingExistingNumbers(
            response.phoneNumberExistenceResults ?? [],
            requestedNumberTypesByNumber
          ),
      }
    );

  const hasExistingNumbers =
    !isPortingTypeInternal && existingNumbers && existingNumbers.length > 0;

  const smsHostabilityResults = PortingQueries.useCheckSmsHostabilityBatch(
    isSmsHosting ? smsNumberValues ?? [] : [],
    isSmsHosting
  );

  const nonHostableNumbers = smsHostabilityResults
    .filter((r) => r.isSuccess && !r.data?.validationPassed)
    .map((r) => r.data?.phoneNumber)
    .filter(Boolean);

  const isSmsHostabilityLoading = smsHostabilityResults.some((r) => r.isLoading);
  const hasNonHostableNumbers = nonHostableNumbers.length > 0;

  const {
    requestedPortDate: requestedPortDateVal,
    asapPort: asapPortVal,
    internalPorting: internalPortingCheckboxValue,
  } = values;
  useEffect(() => {
    if (requestedPortDateVal) {
      const portDate = getUTCDateWithPortingTimeString(requestedPortDateVal);
      if (isHoliday(portDate) || isWeekend(portDate)) {
        setRequestPortDateError(
          'Please select date which does not fall on weekend or holiday'
        );
      } else {
        setRequestPortDateError('');
      }
    }
  }, [requestedPortDateVal]);

  useEffect(() => {
    if (isPortingTypeInternal) {
      getInternalPortingNumberData();
      initValues();
    }
  }, [internalPortingValue]);

  useEffect(() => {
    setRequestPortDateError('');
  }, [asapPortVal]);

  useEffect(() => {
    internalPortingCheckboxValue
      ? setInternalPortingValue('PORTING_TYPE_INTERNAL')
      : setInternalPortingValue('PORTING_TYPE_MAIN');
  }, [internalPortingCheckboxValue]);

  useEffect(() => {
    window.scroll(0, 0);

    setError('');
    initValues();
  }, []);

  const closeModal = () => {
    closePortSubmissionModal();
    if (messageModal.success) {
      if (portingData && portingData !== ({} as PortingData)) {
        history.replace(
          injectParams(ONBOARDING.portingV2Detail, {
            id: currentLocation?.LocationID,
            portingDataId: portingData?.id,
          })
        );
      } else {
        goBack();
      }
    }
  };
  const getPhoneProvider = () => {
    if (internalPortingNumberError || internalPortingFaxError) {
      return '';
    } else if (providerNumMap.size === 1) {
      return providerNumMap?.keys()?.next()?.value;
    } else if (providerFaxMap.size === 1) {
      return providerFaxMap?.keys()?.next()?.value;
    } else return '';
  };

  const getProviderFromMap = (providerMap) => {
    const telnyxPin = '0096';
    if (providerMap) {
      switch (providerMap?.keys()?.next()?.value?.toLowerCase()) {
        case 'bandwidth':
          return currentLocation?.ParentID
            ? currentLocation?.ParentID?.toString()?.slice(-4)
            : currentLocation?.LocationID?.toString()?.slice(-4);
        case 'telnyx':
          return telnyxPin;
      }
    }
    return '';
  };

  const getPinBasedOnProvider = () => {
    if (internalPortingNumberError || internalPortingFaxError) {
      return '';
    }
    let mapValue;
    if (providerNumMap.size === 1) {
      mapValue = providerNumMap;
    } else if (providerFaxMap.size === 1) {
      mapValue = providerFaxMap;
    } else {
      mapValue = '';
    }
    return getProviderFromMap(mapValue);
  };

  const getAccountNumber = () => {
    if (isPortingTypeInternal && (voiceNumbers || faxNumbers)) {
      let value;
      if (voiceNumberValues) {
        return (value = voiceNumberValues?.[0]);
      } else if (faxNumberValues) {
        return (value = faxNumberValues?.[0]);
      } else return (value = '');
    }
    return '';
  };

  const initValues = () => {
    getIntialValues();
    seedValues({
      companyName: autoFillDetails.companyName || '',
      serviceAddress: autoFillDetails.serviceAddress || '',
      city: autoFillDetails.city || '',
      postalCode: autoFillDetails.postalCode || '',
      phoneProvider: getPhoneProvider() || '',
      state: autoFillDetails.state || '',
      country: autoFillDetails.country || '',
      accountNumber: getAccountNumber(),
      authorizedUserFullName: autoFillDetails.authorizedUserFullName || '',
      accountPin: getPinBasedOnProvider(),
    });
  };

  const internalPortingValueCheck = () => {
    seedValues({
      internalPorting: true,
    });
  };
  const internalPortingValueUnCheck = () => {
    seedValues({
      internalPorting: false,
    });
  };

  const handleSubmit = async (values: any) => {
    if (isValidated(values)) {
      setSubmitLoading(true);
      const error: string = await submitPortingData(values);
      if (error) {
        setMessageModal({
          body: error,
          header: 'Something Went Wrong',
          success: false,
        });
      } else {
        setMessageModal({
          header: 'Thank You',
          body: successMessage,
          success: true,
        });
      }
      setSubmitLoading(false);
      portSubmissionTriggerModalProps.onClick();
    } else {
      setSubmitLoading(false);
      setError('Some of the fields does not have proper values');
    }
  };

  const submitPortingData = async (values: any): Promise<string> => {
    try {
      const {
        companyName,
        serviceAddress,
        city,
        postalCode,
        state,
        country,
        phoneProvider: currentServiceProvider,
        accountNumber,
        authorizedUserFullName,
        userOfficeEmail,
        accountPin,
        asapPort,
        requestedPortDate,
      } = values;
      const [firstName, ...restName] = authorizedUserFullName.split(' ');
      const portingRequests: PortingRequest[] = [];
      const portDate =
        asapPort || !requestedPortDate
          ? ''
          : getUTCDateWithPortingTimeString(requestedPortDate);

      if (isSmsHosting) {
        (smsNumberValues ?? []).forEach((number: string) => {
          portingRequests.push({
            phoneNumber: number?.trim(),
            portType: PortType.PORT_TYPE_UNSPECIFIED,
            numberType: NumberType.NUMBER_TYPE_SMS,
            requestedFirmOrderCommitmentDate: portDate,
            smsPortEligible: true,
          });
        });
      } else {
        splitAndTrimString(faxNumbers, ',').forEach((number: string) => {
          portingRequests.push({
            phoneNumber: number?.trim(),
            portType: PortType.PORT_TYPE_UNSPECIFIED,
            numberType: NumberType.NUMBER_TYPE_FAX,
            requestedFirmOrderCommitmentDate: portDate,
          });
        });

        splitAndTrimString(voiceNumbers, ',').forEach((number: string) => {
          portingRequests.push({
            phoneNumber: number?.trim(),
            portType: PortType.PORT_TYPE_UNSPECIFIED,
            numberType: NumberType.NUMBER_TYPE_PHONE,
            requestedFirmOrderCommitmentDate: portDate,
          });
        });
      }
      const voiceNumArr = splitAndTrimString(voiceNumbers, ',');

      // Because of the need to use the ExtendedMediaData type (to include the fileType value),
      // we need to remove it again before sending the data to the schema endpoint.
      const sanitizedFileData = customerAttachedFiles.map((file) => {
        // removes the extended fileType property from the file object.
        const { fileType, ...rest } = file;
        return rest;
      });

      const response = await portingSchemaApi.createPortingData({
        portingData: {
          locationId: currentLocation?.LocationID,
          authorizedUserFirstName: firstName,
          authorizedUserLastName: restName.join(' '),
          serviceStreet1: serviceAddress,
          serviceCity: city,
          serviceState: state,
          serviceCountry: country,
          serviceZip: postalCode,
          companyName: companyName,
          phoneServiceAccountNumber: accountNumber,
          portingRequests: portingRequests,
          billingPhoneNumber: isSmsHosting ? '' : voiceNumArr[0] ?? '',
          currentPhoneServiceProvider: currentServiceProvider,
          officeEmail: userOfficeEmail,
          requestClient: 'WAM',
          accountPin: accountPin,
          requestAsapPort: isSmsHosting ? false : asapPort,
          customerPhoneBillMedia: sanitizedFileData, // The schema endpoint seems to do nothing with this field except return it in the response.
          noBillAvailable: isSmsHosting ? true : noBillAvailable,
          reasonBillNotAvailable: isSmsHosting
            ? ''
            : noBillAvailable
            ? commentField.value
            : '',
          portType:
            internalPortingValue === 'PORTING_TYPE_INTERNAL'
              ? PortingType.PORTING_TYPE_INTERNAL
              : PortingType.PORTING_TYPE_MAIN,
        },
      });

      // const portingDataId = response.portingData?.[0].id ?? '';

      // try {
      //   // Since the schema endpoint does not handle the customerPhoneBillMedia field, we need to
      //   // upload the files after the porting data has been created.
      //   for (const file of customerAttachedFiles) {
      //     if (file.mediaType === MediaType.MEDIA_TYPE_LOA) {
      //       // The schema endpoint only handles the phoneBill media type, so we need to use the
      //       // old endpoint to upload the LOA file for now.
      //       await portViewLoaUpload(portingDataId, {
      //         porting_data_id: portingDataId,
      //         media_id: file.mediaId ?? '',
      //         file_name: file.fileName ?? '',
      //         media_type: OldMediaType.LOA,
      //       });
      //     } else {
      //       await portingSchemaApi.uploadPortOrderDocument({
      //         contentType: file.fileType,
      //         mediaData: {
      //           portingDataId: portingDataId,
      //           mediaId: file.mediaId,
      //           fileName: file.fileName,
      //           mediaType: OldMediaType.phoneBill,
      //         },
      //       });
      //     }
      //   }
      // } catch (error) {
      //   console.error(error);
      //   alert.error(`Error uploading file`);
      // }

      setPortingData(response.portingData?.[0] as PortingData);
      return '';
    } catch (error) {
      console.error(error);
      const message = getErrorMessageFromResponseError(error);
      return message || 'Could not submit porting numbers. Please try again later.';
    }
  };

  const isValidated = ({ asapPort, requestedPortDate }): boolean =>
    validatePhoneNumberFields() && validateRequestPortFields(asapPort, requestedPortDate);

  const validateRequestPortFields = (asapPort, requestedPortDate): boolean => {
    let validate = true;

    if (
      !asapPort &&
      !!requestedPortDate &&
      (isHoliday(getUTCDateWithPortingTimeString(requestedPortDate)) ||
        isWeekend(getUTCDateWithPortingTimeString(requestedPortDate)))
    ) {
      validate = false;
      setRequestPortDateError(
        'Please select date which does not fall on weekend or holiday'
      );
    } else {
      setRequestPortDateError('');
    }
    return validate;
  };

  const validatePhoneNumberFields = () => {
    let validate = true;
    if (isSmsHosting) {
      if (smsNumbers.trim() === '') {
        setPhoneNumbersError('SMS number is required to be filled');
        validate = false;
      } else {
        setPhoneNumbersError('');
      }

      if (!validatePhoneNumbersRegex(smsNumbers)) {
        validate = false;
      }
    } else {
      if (voiceNumbers.trim() === '' && faxNumbers.trim() === '') {
        setPhoneNumbersError('Atleast one voice/fax number is required to be filled');
        validate = false;
      } else {
        setPhoneNumbersError('');
      }

      if (!validatePhoneNumbersRegex(voiceNumbers)) {
        validate = false;
      }

      if (!validatePhoneNumbersRegex(faxNumbers)) {
        validate = false;
      }
    }

    return validate;
  };

  const goBack = () => {
    initValues();
    history.push(injectParams(ONBOARDING.portingV2, { id: currentLocation?.LocationID }));
  };

  const isFormComplete = isComplete && values.country && values.state;

  const enableSubmit = isSmsHosting
    ? isFormComplete &&
      !isValidatingNumbers &&
      !hasExistingNumbers &&
      !duplicateSmsError &&
      !isSmsHostabilityLoading &&
      !hasNonHostableNumbers
    : isFormComplete &&
      (attachedBills.length > 0 ||
        (noBillAvailable && (!checkCountryValue || commentField.value !== ''))) &&
      !duplicateFaxError &&
      !duplicatePhnError &&
      !isValidatingNumbers &&
      !hasExistingNumbers;

  const handleFileUpload = async (file: File, type: PortingMediaType) => {
    if (file !== undefined) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        // We can not use the schema endpoint to upload the file as it requires the portingDataId
        // but we do not have a a portingDataId at this point since we are barely creating the porting data.
        // const data = await portingSchemaApi.uploadPortOrderDocument({});
        const data = await API.uploadPhoneFile(formData);
        setUploadedFileUrl(data);
        alert.success(`Successfully submitted file: ${file.name}`);
        return data;
      } catch {
        alert.error('Error uploading file.');
        return null;
      }
    }
    return null;
  };

  const isSameFileAlreadyUploaded = (file: File, currentFiles: MediaData[]) => {
    return currentFiles.some((item) => item.fileName === file.name);
  };

  const validateFile = (acceptedFiles: File[], currentFiles: MediaData[]) => {
    if (acceptedFiles.length > 0) {
      const sortedFiles = acceptedFiles.reduce(
        (acc, file) => {
          const isDuplicate = isSameFileAlreadyUploaded(file, currentFiles);
          if (file.size <= 3e6 && !isDuplicate) {
            return { ...acc, accepted: [...acc.accepted, file] };
          } else if (isDuplicate) {
            return { ...acc, duplicates: [...acc.duplicates, file] };
          } else {
            return { ...acc, rejected: [...acc.rejected, file] };
          }
        },
        { accepted: [], rejected: [], duplicates: [] } as SortedFiles
      );
      return sortedFiles;
    }
    return { accepted: [], rejected: [], duplicates: [] } as SortedFiles;
  };

  const showAlerts = (sortedFile: SortedFiles) => {
    sortedFile.rejected.forEach((file) => {
      alert.error(`Error uploading file: ${file.name}`);
    });
    sortedFile.duplicates.forEach((file) => {
      alert.error(
        `The file with name ${file.name} has been already uploaded. Please upload with different name.`
      );
    });
  };

  const onLoaDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length + attachedLOA.length > 1) {
        alert.error('You can add only one LOA file.');
        return;
      }
      setLoaLoader(true);
      const sortedFile = validateFile(acceptedFiles, attachedLOA);
      if (sortedFile.accepted.length > 0) {
        const responseData: PortCreationFileResponseData | null = await handleFileUpload(
          sortedFile?.accepted?.[0],
          PortingMediaType.MEDIA_TYPE_LOA
        );
        if (responseData?.ID) {
          setAttachedLOA([
            {
              mediaId: responseData?.ID,
              fileName: responseData?.Name,
              mediaType: PortingMediaType.MEDIA_TYPE_LOA,
              fileType: sortedFile.accepted[0].type,
            },
          ]);
        }
      }
      setLoaLoader(false);
      showAlerts(sortedFile);
    },
    [attachedLOA.length]
  );

  const onBillsDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length + attachedBills.length > 5) {
        alert.error('You can add a maximum of 5 previous bills');
        return;
      }
      setBillLoader(true);
      const sortedFile = validateFile(acceptedFiles, attachedBills);
      sortedFile.accepted.forEach(async (file) => {
        const responseData: PortCreationFileResponseData | null = await handleFileUpload(
          file,
          PortingMediaType.MEDIA_TYPE_PHONE_BILL
        );
        if (responseData?.ID) {
          const acceptedFiles: ExtendedMediaData[] = [];
          acceptedFiles.push({
            mediaId: responseData?.ID,
            fileName: responseData?.Name,
            mediaType: PortingMediaType.MEDIA_TYPE_PHONE_BILL,
            fileType: file.type,
          });
          setAttachedBills((prevBills) => [...prevBills, ...acceptedFiles]);
        }
      });
      setBillLoader(false);
      showAlerts(sortedFile);
    },
    [attachedBills.length]
  );

  useEffect(() => {
    if (attachedBills.length === 0) {
      setNoBillAvailable(true);
    } else {
      setNoBillAvailable(false);
    }
    if (attachedLOA.length === 0 && isFormComplete) {
      setError('LOA is a required field.');
    }
    if (isSmsHosting) {
      setError('');
    } else if (!checkCountryValue && isFormComplete) {
      setError('');
    } else if (
      ((!noBillAvailable && attachedBills.length === 0) ||
        (noBillAvailable && commentField.value === '')) &&
      isFormComplete
    ) {
      setError('Please upload bills or provide the reasons for not providing bill.');
    } else {
      setError('');
    }
    setCustomerAttachedFiles(
      isSmsHosting ? [...attachedLOA] : [...attachedBills, ...attachedLOA]
    );
  }, [
    attachedBills,
    attachedLOA,
    isFormComplete,
    noBillAvailable,
    commentField.value,
    isSmsHosting,
  ]);

  const removeLOA = (file: MediaData) => {
    const tempIndex = attachedLOA.findIndex((item) => item.mediaId === file.mediaId);
    attachedLOA.splice(tempIndex, 1);
    setAttachedLOA([...attachedLOA]);
  };

  const removeBills = (file: MediaData) => {
    const tempIndex = attachedBills.findIndex((item) => item.mediaId === file.mediaId);
    attachedBills.splice(tempIndex, 1);
    setAttachedBills([...attachedBills]);
  };

  const onRemoveFile = async (file: MediaData) => {
    if (!file.mediaId) return;

    try {
      // We can not use the schema endpoint to delete the file as it requires the portingDataId
      // but we do not have a a portingDataId at this point since we are barely creating the portingData.
      // PT-TODO: need to discuss this with the team to see if we update this endpoint to make the portingDataId optional.
      // await portingSchemaApi.deletePortOrderDocument({portingDataId: '', mediaDataId: file.mediaId})
      await API.deleteFile(file.mediaType as OldMediaType, file.mediaId);
      const tempIndex = customerAttachedFiles.findIndex(
        (item) => item.mediaId === file.mediaId
      );
      customerAttachedFiles.splice(tempIndex, 1);
      setCustomerAttachedFiles([...customerAttachedFiles]);
      if (file.mediaType === PortingMediaType.MEDIA_TYPE_LOA) {
        removeLOA(file);
      } else {
        removeBills(file);
      }
    } catch {
      alert.error(`deletion failed: ${file.fileName}`);
    }
  };

  const onCancel = () => {
    customerAttachedFiles.forEach((item) => {
      onRemoveFile(item);
    });
    cancelConfirmationModalProps.onClose();
    goBack();
  };

  const onCancelInternalPorting = () => {
    setInternalPortingValue('PORTING_TYPE_INTERNAL');
    internalPortingValueCheck();
    cancelInternalPorting.onClose();
  };
  const setBackToMainPorting = () => {
    internalPortingValueUnCheck();
    cancelInternalPorting.onClose();
  };

  const handleDownloadBill = async (mediaObj, file_name) => {
    const { LocationID } = mediaObj;
    const payload = {
      customer_phone_bill_media_id: mediaObj.ID,
      customer_phone_bill_media_type: mediaObj.Type,
      location_id: LocationID,
    };
    setDownloadLOALoading(true);
    try {
      const response = await portViewBillDownload(payload);
      const file = response.data;
      fileDownload(file, file_name, 'application/pdf');
    } catch (err) {
      alert.error(err.message);
    } finally {
      setDownloadLOALoading(false);
    }
  };

  return (
    <Page
      title="Port Order"
      subtitle="Create new port order by filling information below"
      containerStyle={css({ position: 'relative' })}
    >
      <div>
        <div css={portOrderStyles.backButtonStyle}>
          <TextLink onClick={goBack}>{'< Back'}</TextLink>
        </div>
      </div>
      <div css={portOrderStyles.container}>
        <div css={portOrderStyles.header}>
          <Heading level={3} css={{ margin: 0 }}>
            New Port Order
          </Heading>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
            `}
          >
            <DropdownField
              {...getFieldProps('portOrderType')}
              label="Port Order Type"
              name="portOrderType"
              css={css`
                width: 200px;
                margin: 0;
              `}
            >
              <DropdownField.Option value={PortingType.PORTING_TYPE_MAIN}>
                Full Port
              </DropdownField.Option>
              <DropdownField.Option value={PortingType.PORTING_TYPE_UNKNOWN}>
                SMS Hosting
              </DropdownField.Option>
            </DropdownField>
            {!isSmsHosting && (
              <CheckboxField
                {...getFieldProps('internalPorting')}
                name="internalPorting"
                label="Internal Porting"
              />
            )}
          </div>
        </div>
        <form
          {...portingFormProps}
          css={css`
            padding: 25px;
          `}
        >
          <div css={portOrderStyles.portingInfoContainer}>
            <Text css={portOrderStyles.title}>Porting Information</Text>
          </div>

          <div css={portOrderStyles.portingNumbers}>
            {isSmsHosting ? (
              <div
                css={css`
                  display: flex;
                `}
              >
                <Text
                  css={css`
                    width: 250px;
                  `}
                >
                  SMS Numbers
                </Text>

                <FormRow>
                  <TextField
                    {...smsNumbersFieldProps}
                    name="smsNumbers"
                    label="SMS Numbers"
                    helperText="Ex: +11234567890, +1 (987) 654-3456..."
                    css={css`
                      width: 230px;
                    `}
                  />
                  {duplicateSmsError && (
                    <Text color="error">Please remove the duplicate Number </Text>
                  )}
                  {hasNonHostableNumbers && (
                    <Text color="error">
                      These numbers are not eligible for SMS hosting:{' '}
                      {nonHostableNumbers.join(', ')}
                    </Text>
                  )}
                </FormRow>
              </div>
            ) : (
              <>
                <div
                  css={css`
                    display: flex;
                  `}
                >
                  <Text
                    css={css`
                      width: 250px;
                    `}
                  >
                    Voice Numbers
                  </Text>

                  <FormRow>
                    <TextField
                      {...voiceNumbersFieldProps}
                      name="voiceNumbers"
                      label="Phone Numbers"
                      helperText="Ex: +11234567890, +1 (987) 654-3456..."
                      css={css`
                        width: 230px;
                      `}
                    />
                    {duplicatePhnError && (
                      <Text color="error">Please remove the duplicate Number </Text>
                    )}
                  </FormRow>
                </div>
                {isPortingTypeInternal && internalPortingNumberError && (
                  <Text css={portOrderStyles.errorStyle} color="error">
                    {internalPortingNumberError}
                  </Text>
                )}
                <div
                  css={css`
                    display: flex;
                  `}
                >
                  <Text
                    css={css`
                      width: 250px;
                    `}
                  >
                    Fax Numbers
                  </Text>

                  <FormRow>
                    <TextField
                      {...faxNumbersFieldProps}
                      name="faxNumbers"
                      label="Fax Numbers"
                      helperText="Ex: +11234567890, +1 (987) 654-3456..."
                      css={css`
                        width: 230px;
                      `}
                    />
                    {duplicateFaxError && (
                      <Text color="error">Please remove the duplicate Number </Text>
                    )}
                  </FormRow>
                </div>
                {isPortingTypeInternal && internalPortingFaxError && (
                  <Text css={portOrderStyles.errorStyle} color="error">
                    {internalPortingFaxError}
                  </Text>
                )}
              </>
            )}
            <Text css={portOrderStyles.errorMessage}>{phoneNumbersError}</Text>
            {hasExistingNumbers && (
              <Text color="error" css={portOrderStyles.errorMessage}>
                These numbers are already ported: {existingNumbers.join(', ')}
              </Text>
            )}
            {!isSmsHosting && (
              <>
                <div
                  css={css`
                    display: flex;
                  `}
                >
                  <Text
                    css={css`
                      width: 250px;
                    `}
                  >
                    ASAP Port
                  </Text>

                  <FormRow>
                    <CheckboxField
                      {...getFieldProps('asapPort')}
                      name="asapPort"
                      label="Request ASAP Port"
                    />
                  </FormRow>
                </div>
                {!asapPortVal && (
                  <div
                    css={css`
                      display: flex;
                    `}
                  >
                    <Text
                      css={css`
                        width: 250px;
                      `}
                    >
                      Requested Port Date
                    </Text>

                    <FormRow>
                      <DatePickerField
                        {...getFieldProps('requestedPortDate')}
                        name="requestedPortDate"
                        label="Port Date"
                      />
                    </FormRow>
                  </div>
                )}
              </>
            )}

            <Text color="error" css={portOrderStyles.errorMessage}>
              {requestPortDateError}
            </Text>
          </div>

          <div css={portOrderStyles.portingInfoContainer}>
            <Text css={portOrderStyles.title}>Business Information</Text>
          </div>

          <div css={portOrderStyles.portingNumbers}>
            <div
              css={css`
                display: flex;
              `}
            >
              <Text
                css={css`
                  width: 250px;
                `}
              >
                Company Name
              </Text>

              <FormRow>
                <TextField
                  {...getFieldProps('companyName')}
                  label="Company Name"
                  css={css`
                    width: 230px;
                  `}
                  name="companyName"
                />
              </FormRow>
            </div>

            <div
              css={css`
                display: flex;
              `}
            >
              <Text
                css={css`
                  width: 250px;
                  margin-bottom: 10px;
                `}
              >
                Service Address
              </Text>
              <div>
                <div css={portOrderStyles.streetContainer}>
                  <FormRow>
                    <TextField
                      {...getFieldProps('serviceAddress')}
                      placeholder="Service Address"
                      label="Service Address"
                      css={css`
                        width: 230px;
                      `}
                      name="serviceAddress"
                    />
                  </FormRow>

                  <FormRow>
                    <TextField
                      {...getFieldProps('city')}
                      placeholder="City"
                      label="City"
                      css={css`
                        width: 230px;
                      `}
                      name="city"
                    />
                  </FormRow>
                </div>

                <FormRow>
                  <DropdownField
                    {...getFieldProps('country')}
                    label="Country"
                    name="country"
                    css={css`
                      width: 230px;
                    `}
                  >
                    <DropdownField.Option value={CountryCodes.USA.Code}>
                      {CountryCodes.USA.Label}
                    </DropdownField.Option>
                    <DropdownField.Option value={CountryCodes.Canada.Code}>
                      {CountryCodes.Canada.Label}
                    </DropdownField.Option>
                  </DropdownField>
                </FormRow>

                <div css={portOrderStyles.streetContainer}>
                  <DropdownField
                    {...getFieldProps('state')}
                    placeholder="State"
                    label="State"
                    name="state"
                    css={css`
                      width: 230px;
                    `}
                  >
                    {filteredStates.map((state: any) => (
                      <DropdownField.Option value={state.code} key={state.code}>
                        {state.label}
                      </DropdownField.Option>
                    ))}
                  </DropdownField>

                  <FormRow>
                    <PostalCodeField
                      {...getFieldProps('postalCode')}
                      placeholder="Postal Code"
                      label="Postal Code"
                      css={css`
                        width: 230px;
                      `}
                      name="postalCode"
                    />
                  </FormRow>
                </div>
              </div>
            </div>
          </div>

          <div css={portOrderStyles.portingInfoContainer}>
            <Text css={portOrderStyles.title}>Phone Information</Text>
          </div>

          <div css={portOrderStyles.portingNumbers}>
            <div
              css={css`
                display: flex;
              `}
            >
              <Text
                css={css`
                  width: 250px;
                `}
              >
                Authorized User
              </Text>

              <FormRow>
                <TextField
                  {...getFieldProps('authorizedUserFullName')}
                  placeholder="Full Name"
                  label="Full Name"
                  css={css`
                    width: 230px;
                  `}
                  name="authorizedUserFullName"
                />
              </FormRow>
            </div>

            {!isSmsHosting && (
              <div
                css={css`
                  display: flex;
                `}
              >
                <Text
                  css={css`
                    width: 250px;
                  `}
                >
                  Account Number
                </Text>

                <FormRow>
                  <TextField
                    {...getFieldProps('accountNumber')}
                    placeholder="Account Number"
                    label="Account Number"
                    css={css`
                      width: 230px;
                    `}
                    name="accountNumber"
                  />
                </FormRow>
              </div>
            )}

            <div
              css={css`
                display: flex;
              `}
            >
              <Text
                css={css`
                  width: 250px;
                `}
              >
                User Office Email
              </Text>

              <FormRow>
                <EmailField
                  {...getFieldProps('userOfficeEmail')}
                  placeholder="Office Email"
                  label="Office Email"
                  css={css`
                    width: 230px;
                  `}
                  name="userOfficeEmail"
                />
              </FormRow>
            </div>

            <div
              css={css`
                display: flex;
              `}
            >
              <Text
                css={css`
                  width: 250px;
                `}
              >
                Provider
              </Text>

              <FormRow>
                <TextField
                  {...getFieldProps('phoneProvider')}
                  placeholder="Phone Provider"
                  label="Phone Provider"
                  css={css`
                    width: 230px;
                  `}
                  name="phoneProvider"
                />
              </FormRow>
            </div>
            {!isSmsHosting && (
              <div
                css={css`
                  display: flex;
                `}
              >
                <Text
                  css={css`
                    width: 250px;
                  `}
                >
                  Account PIN
                </Text>

                <FormRow>
                  <TextField
                    {...getFieldProps('accountPin')}
                    placeholder="Account PIN"
                    label="PIN"
                    css={css`
                      width: 230px;
                    `}
                    name="accountPin"
                  />
                </FormRow>
              </div>
            )}

            <div
              css={css`
                display: flex;
              `}
            >
              <Text
                css={css`
                  width: 250px;
                `}
              >
                LOA
              </Text>
              <FormRow>
                {attachedLOA.length <= 0 && (
                  <PortOrderUploadComponent onFileDrop={onLoaDrop} />
                )}
                {attachedLOA.length > 0 && (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <Text
                      css={css`
                        padding-right: 10px;
                        margin: 0;
                        cursor: pointer;
                        color: #28caf4;
                      `}
                      onClick={() => {
                        handleDownloadBill(uploadedFileUrl, attachedLOA[0].fileName);
                      }}
                    >
                      {attachedLOA[0].fileName}
                    </Text>
                    <TrashIcon
                      onClick={() => onRemoveFile(attachedLOA[0])}
                      css={css`
                        cursor: pointer;
                      `}
                      color="light"
                    />
                  </div>
                )}
                {!!loaLoader && <SpinningLoader size="small" />}
              </FormRow>
            </div>

            {!isSmsHosting && (
              <div
                css={css`
                  display: flex;
                `}
              >
                <Text
                  css={css`
                    width: 250px;
                  `}
                >
                  Previous Bill
                </Text>
                <FormRow>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                      `}
                    >
                      <PortOrderUploadComponent
                        onFileDrop={onBillsDrop}
                        canUploadMutipleFiles={true}
                      />
                      {!!billLoader && <SpinningLoader size="small" />}
                    </div>
                    {attachedBills.length > 0 && (
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          margin-bottom: 10px;
                        `}
                      >
                        {attachedBills.map((item, index) => {
                          return (
                            <div
                              key={item.fileName}
                              css={css`
                                display: flex;
                                align-items: center;
                              `}
                            >
                              <TextLink
                                css={css`
                                  padding-right: 10px;
                                  margin: 0;
                                `}
                              >
                                <div
                                  css={css`
                                    display: flex;
                                  `}
                                >
                                  <Text
                                    css={css`
                                      color: #28caf4;
                                      margin: auto;
                                      cursor: pointer;
                                    `}
                                    onClick={() => {
                                      handleDownloadBill(uploadedFileUrl, item.fileName);
                                    }}
                                  >
                                    {item.fileName}
                                  </Text>
                                  <FileButton
                                    key={item.fileName}
                                    file={item}
                                    onRemove={onRemoveFile}
                                  ></FileButton>
                                </div>
                              </TextLink>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {attachedBills.length === 0 && (
                      <>
                        <CheckboxField
                          {...checkNoBillField}
                          name="noBillAvailable"
                          label="No Bill Available"
                          css={css`
                            margin-top: 10px;
                          `}
                        ></CheckboxField>

                        {!!checkNoBillField.value && (
                          <TextareaField
                            {...commentField}
                            name="noBillComments"
                            label="Why isn't there a bill?"
                            css={css`
                              margin-top: ${theme.spacing(3)};
                              height: 80px;
                            `}
                          ></TextareaField>
                        )}
                      </>
                    )}
                  </div>
                </FormRow>
              </div>
            )}
          </div>

          <div css={portOrderStyles.buttonContainers}>
            <div css={portOrderStyles.errorMessage}>{error}</div>
            <ButtonBar css={portOrderStyles.buttonContainersColor}>
              <SecondaryButton
                onClick={() => {
                  triggerCancelProps.onClick();
                }}
                css={portOrderStyles.submitButtonStyle}
              >
                Cancel
              </SecondaryButton>

              <PrimaryButton
                css={portOrderStyles.submitButtonStyle}
                color="blue"
                size="large"
                type="submit"
                disabled={submitLoading || !enableSubmit}
              >
                Submit
              </PrimaryButton>
            </ButtonBar>
          </div>
        </form>
      </div>

      <ContentLoader
        show={submitLoading || downloadLOALoading}
        message={downloadLOALoading ? 'Downloading...' : 'Submitting...'}
      />

      <MessageDialogue
        header={messageModal.header}
        body={messageModal.body}
        messageModalProps={portSubmissionModalProps}
        closeMessageModal={closeModal}
        actionLabel="Okay"
        onActionLabelClick={closeModal}
      />

      <Modal {...cancelConfirmationModalProps}>
        <Modal.Header>Confirm Cancel</Modal.Header>
        <Modal.Body>
          <Text textAlign="center">
            Are you sure you want to cancel the porting request. All the current uploaded
            files will be deleted on this process.
          </Text>
        </Modal.Body>
        <ButtonBar>
          <SecondaryButton onClick={closeCancelModal}>No, do not cancel.</SecondaryButton>
          <PrimaryButton destructive onClick={onCancel}>
            Yes, cancel.
          </PrimaryButton>
        </ButtonBar>
      </Modal>
      <Modal {...cancelInternalPorting}>
        <Modal.Header>Confirm Cancel</Modal.Header>
        <Modal.Body>
          <Text textAlign="center">
            Checking Internal Port 'ON' would reset the Business and Phone information.
            Would you like to proceed?
          </Text>
        </Modal.Body>
        <ButtonBar>
          <SecondaryButton onClick={setBackToMainPorting}>
            No, do not remove.
          </SecondaryButton>
          <PrimaryButton destructive onClick={onCancelInternalPorting}>
            Yes, Remove.
          </PrimaryButton>
        </ButtonBar>
      </Modal>
    </Page>
  );
};

type FileProps = {
  file: MediaData;
  onRemove: (file: MediaData) => void;
  onDownload?: (file: MediaData) => void;
};

export const FileButton = ({ file, onRemove, onDownload = noop }: FileProps) => {
  return (
    <div
      css={css`
        margin: 5px 0;
        display: flex;
        align-items: center;
      `}
    >
      {onDownload !== noop && (
        <TextLink
          css={css`
            padding-right: 10px;
            margin: 0;
          `}
          onClick={() => onDownload(file)}
        >
          {file.fileName}
        </TextLink>
      )}
      <IconButton label="Delete file" size="small">
        <TrashIcon
          onClick={() => onRemove(file)}
          css={css`
            cursor: pointer;
          `}
          color="light"
        />
      </IconButton>
    </div>
  );
};
